'use client';

import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DownloadAttachmentButton } from '@/components/common/button/DownloadAttachmentButton';
import type { AttachmentSliderItem } from '../static';

type AttachmentSliderSectionProps = {
  heading?: string;
  description?: string;
  items: AttachmentSliderItem[];
  visibleItems?: number;
  add_background?: boolean;
  add_bottom_border?: boolean;
};

function usePerPage(desktopCount: number) {
  const [perPage, setPerPage] = useState(desktopCount);

  useEffect(() => {
    const update = () => {
      setPerPage(window.innerWidth < 768 ? 1 : desktopCount);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [desktopCount]);

  return perPage;
}

export default function AttachmentSliderSection({
  heading,
  description,
  items,
  visibleItems = 3,
  add_background = false,
  add_bottom_border = false,
}: AttachmentSliderSectionProps) {
  const desktopCount = Math.min(Math.max(Number(visibleItems) || 3, 1), 4);
  const perPage = usePerPage(desktopCount);
  const [page, setPage] = useState(0);

  // Reset to first page whenever perPage changes
  useEffect(() => { setPage(0); }, [perPage]);

  const totalPages = Math.ceil(items.length / perPage);

  const content = (
    <div className={add_bottom_border ? 'pb-8 border-b border-gray-200' : ''}>
      {heading && (
        <h2 className="text-3xl md:text-4xl font-bold text-primary-700 mb-4">
          {heading}
        </h2>
      )}
      {description && (
        <p className="text-gray-600 mb-8">{description}</p>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          aria-label="Previous"
          className="flex-shrink-0 p-1 text-gray-700 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        <div className="flex-1 overflow-hidden">
          <div
            className="flex transition-transform duration-300 ease-in-out"
            style={{ transform: `translateX(-${page * 100}%)` }}
          >
            {Array.from({ length: totalPages }).map((_, pageIdx) => (
              <div
                key={pageIdx}
                className="min-w-full grid gap-4"
                style={{ gridTemplateColumns: `repeat(${perPage}, minmax(0, 1fr))` }}
              >
                {items.slice(pageIdx * perPage, pageIdx * perPage + perPage).map((item, idx) => (
                  <a
                    key={pageIdx * perPage + idx}
                    href={item.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <DownloadAttachmentButton
                      className="w-full justify-start text-base font-semibold py-5 px-5"
                      label={item.title}
                    />
                  </a>
                ))}
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={page >= totalPages - 1}
          aria-label="Next"
          className="flex-shrink-0 p-1 text-gray-700 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>
    </div>
  );

  if (add_background) {
    return (
      <section className="w-screen relative left-1/2 right-1/2 -mx-[50vw] bg-gradient-to-b from-[rgba(237,237,249,0)] to-[#DEE5E8] py-12">
        <div
          className="absolute left-0 bottom-0 w-[300px] h-[300px] bg-no-repeat bg-left-bottom bg-contain pointer-events-none"
          style={{ backgroundImage: "url(/images/left-corner-supergrafis.png)" }}
        />
        <div className="container max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          {content}
        </div>
      </section>
    );
  }

  return <div className="w-full">{content}</div>;
}
